import Tooltip from "components/Tooltip";
import React from "react";

/**
 * Formats requirement text by bolding interpolated values
 *
 * Compares the original template with the interpolated text and wraps
 * only the replaced values in <b> tags with tooltips
 *
 * @param {string} originalValue - The template string with {{variable}} placeholders
 * @param {string} interpolatedValue - The string with values replaced
 * @returns {React.Element|string} Formatted text with interpolated values bolded and tooltips, or original string if no formatting needed
 */
export default function formatInterpolatedText(originalValue, interpolatedValue) {
  // Defensive checks for invalid inputs
  if (!originalValue || !interpolatedValue) return interpolatedValue;
  if (typeof originalValue !== "string" || typeof interpolatedValue !== "string") return interpolatedValue;

  // If they're the same, no interpolation occurred
  if (originalValue === interpolatedValue) return interpolatedValue;

  const variableRegex = /{{(.*?)}}/g;
  const variables = [];
  let match;

  // Extract all template variable names
  while ((match = variableRegex.exec(originalValue)) !== null) {
    variables.push(match[1].trim()); // Trim to handle spaces in variable names
  }

  // If no template variables found, return as-is
  if (!variables.length) return interpolatedValue;

  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  let pattern = escapeRegex(originalValue);
  variables.forEach((variable) => {
    pattern = pattern.replace(escapeRegex(`{{${variable}}}`), "(.+?)");
  });

  const regex = new RegExp(`^${pattern}$`);
  const result = interpolatedValue.match(regex);

  if (!result) return interpolatedValue;

  const capturedValues = result.slice(1);

  let lastIndex = 0;
  const parts = [];

  capturedValues.forEach((value, index) => {
    const valueIndex = interpolatedValue.indexOf(value, lastIndex);

    if (valueIndex > lastIndex) {
      parts.push(interpolatedValue.slice(lastIndex, valueIndex));
    }

    parts.push(
      <Tooltip tooltipId="RoleRequirements.InterpolatedValueTooltip" key={`tooltip-${index}`}>
        <b key={`interpolated-value-${index}`}>{value}</b>
      </Tooltip>
    );

    lastIndex = valueIndex + value.length;
  });

  if (lastIndex < interpolatedValue.length) {
    parts.push(interpolatedValue.slice(lastIndex));
  }

  return <>{parts}</>;
}
